import { getApiBaseUrl } from './utils/environment.mjs';
import { createContext, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { apiService } from "./middleware/refreshToken.mjs";
import { secureLogger } from "./utils/secureLogger.mjs";
import { clearUserContext } from "./utils/sentry.mjs";

export const AuthContext = createContext();

const API_URL = getApiBaseUrl();

const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    // Set when the backend reports the account has been suspended, so the app can
    // block access rather than failing request-by-request.
    const [isSuspended, setIsSuspended] = useState(false);
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isAuditor, setIsAuditor] = useState(false);
    const [isRegionalDirector, setIsRegionalDirector] = useState(false);
    const [isGlobalDirector, setIsGlobalDirector] = useState(false);
    const [isFinanceDirector, setIsFinanceDirector] = useState(false);
    // Standing in for an auditor. Server-decided (from the accepted,
    // unexpired delegation) and sent on /auth/status - the client cannot
    // work it out from the role, which is why a stand-in never saw the card.
    const [isAuditorDelegate, setIsAuditorDelegate] = useState(false);
    const [auditorCover, setAuditorCover] = useState(null);
    // Reports this person was named to approve. Server-decided, and
    // deliberately null for a finance director, who has their own board.
    const [reportApproval, setReportApproval] = useState(null);
    // Approval a regional or global director has handed to this person
    // (capabilities.directorCover), for the note on their dashboard card.
    const [directorCover, setDirectorCover] = useState(null);
    const [isEastAfricaStaff, setEastAfricaStaff] = useState(false);
    const [skipAuthCheck, setSkipAuthCheck] = useState(false);
    const navigate = useNavigate();

    // Helper function to check if user is an OAuth user
    const isOAuthUser = useCallback((userData) => {
        return userData?.authProvider && userData.authProvider !== 'local';
    }, []);

    // Helper function to check if user needs profile completion
    const needsProfileCompletion = useCallback((userData) => {
        return userData && (!userData.region || !userData.position);
    }, []);

    // Helper function to update user-related states
    const updateUserStates = useCallback((userData) => {
        if (userData && typeof userData === 'object') {
            const userRole = userData.role;
            const userRegion = userData.region;

            setUser(userData);
            setIsAdmin(userRole === 'admin');
            setIsAuditor(userRole === 'auditor');
            setIsRegionalDirector(userRole === 'regional_director');
            setIsGlobalDirector(userRole === 'global_director');
            setIsFinanceDirector(userRole === 'finance_director');
            setIsAuditorDelegate(!!userData.capabilities?.auditor);
            setAuditorCover(userData.capabilities?.auditor || null);
            setReportApproval(userData.capabilities?.reportApprover || null);
            setDirectorCover(userData.capabilities?.directorCover || null);
            setEastAfricaStaff(userRegion === "East-Africa");
            setIsAuthenticated(true);
        } else {
            setUser(null);
            setIsAdmin(false);
            setIsAuditor(false);
            setIsRegionalDirector(false);
            setIsGlobalDirector(false);
            setIsFinanceDirector(false);
            setIsAuditorDelegate(false);
            setAuditorCover(null);
            setReportApproval(null);
            setDirectorCover(null);
            setEastAfricaStaff(false);
            setIsAuthenticated(false);
        }
    }, []);

    const checkAuthStatus = async (silent = false) => {
        // Don't check auth status if we're in the middle of verification process
        if (skipAuthCheck) {
            setIsLoading(false);
            return;
        }

        if (!silent) setIsLoading(true);
        try {
            const response = await axios.get(`${API_URL}/auth/status`, { 
                withCredentials: true 
            });
                  
            const { isAuthenticated: authStatus, user: userData } = response.data;
            
            if (authStatus && userData) {
                updateUserStates(userData);
            } else {
                setIsAuthenticated(false);
                setUser(null);
                setIsAuditor(false);
                setIsAdmin(false);
                setIsRegionalDirector(false);
                setIsGlobalDirector(false);
                setIsFinanceDirector(false);
                setIsAuditorDelegate(false);
                setAuditorCover(null);
                setReportApproval(null);
                setDirectorCover(null);
                setEastAfricaStaff(false);
            }
        } catch (error) {
            secureLogger.error("Auth check failed", { status: error.response?.status });
            // Rate limited - server hasn't said we're unauthenticated, keep current state
            if (error.response?.status === 429) {
                return;
            }
            // Suspended mid-session: surface it explicitly instead of looking
            // like a random auth failure.
            if (error.response?.data?.errorCode === 'ACCOUNT_SUSPENDED') {
                setIsSuspended(true);
            }
            // Reset all states on actual auth failure
            setIsAuthenticated(false);
            setUser(null);
            setIsAuditor(false);
            setIsAdmin(false);
            setIsRegionalDirector(false);
            setIsGlobalDirector(false);
            setIsFinanceDirector(false);
            setIsAuditorDelegate(false);
            setAuditorCover(null);
            setReportApproval(null);
            setDirectorCover(null);
            setEastAfricaStaff(false);
        } finally {
            setIsLoading(false);
        }
    };

    // SINGLE useEffect for all initialization
    useEffect(() => {
        // Check if we're on the verification page
        const currentPath = window.location.pathname;
        if (currentPath === '/verify-email') {
            setSkipAuthCheck(true);
            setIsLoading(false);
            return; // Exit early, don't set up other stuff
        }

        // Initial auth check
        checkAuthStatus();

        // Listen for user updates from token refresh.
        // SECURITY: Never trust the event payload directly - window events can be
        // dispatched by any script on the page. Always re-verify with the server.
        const handleUserUpdate = () => {
            checkAuthStatus(true); // silent - don't show loading spinner on background refresh
        };

        window.addEventListener('userUpdated', handleUserUpdate);

        // Cleanup function
        return () => {
            window.removeEventListener('userUpdated', handleUserUpdate);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array - run only once on mount

    const logout = async () => {
        // SECURITY: Clear session storage and API state on logout.
        // Auth tokens live in httpOnly cookies (cleared server-side); localStorage
        // holds only non-sensitive UI preferences and must NOT be wiped on logout.
        const clearAllClientData = () => {
            try {
                sessionStorage.clear();
                apiService.clearAuth();
                clearUserContext();
            } catch (e) {
                secureLogger.warn("Could not clear browser storage");
            }
        };

        try {
            await axios.post(`${API_URL}/auth/logout`, {}, {
                withCredentials: true
            });

            setIsAuthenticated(false);
            setUser(null);
            setIsAuditor(false);
            setIsAdmin(false);
            setIsRegionalDirector(false);
            setIsGlobalDirector(false);
            setIsFinanceDirector(false);
            setEastAfricaStaff(false);
            setSkipAuthCheck(false);
            clearAllClientData();

            navigate("/");
        } catch (error) {
            secureLogger.error("Logout failed", { status: error.response?.status });
            // Still logout locally even if server request fails
            setIsAuthenticated(false);
            setUser(null);
            setIsAdmin(false);
            setIsAuditor(false);
            setIsRegionalDirector(false);
            setIsGlobalDirector(false);
            setIsFinanceDirector(false);
            setEastAfricaStaff(false);
            setSkipAuthCheck(false);
            clearAllClientData();
            navigate("/");
        }
    };

    // Function to manually set authentication after email verification
    const setAuthenticatedUser = useCallback((userData) => {
        
        updateUserStates(userData);
        setSkipAuthCheck(false); // Re-enable auth checks
    }, [updateUserStates]);

    // Function to start verification process (skip auth checks)
    const startVerificationProcess = useCallback(() => {
        setSkipAuthCheck(true);
        setIsAuthenticated(false);
        setUser(null);
        setIsAuditor(false);
        setIsAdmin(false);
        setIsRegionalDirector(false);
        setIsGlobalDirector(false);
        setIsFinanceDirector(false);
        setEastAfricaStaff(false);
        setIsLoading(false);
    }, []);

    // Function to manually refresh auth state
    const refreshAuthState = useCallback(async () => {
        
        await checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipAuthCheck]);

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                isEastAfricaStaff,
                user,
                isLoading,
                isAdmin,
                isAuditor,
                isRegionalDirector,
                isGlobalDirector,
                isFinanceDirector,
                isAuditorDelegate,
                auditorCover,
                reportApproval,
                directorCover,
                checkAuthStatus,
                refreshAuthState,
                logout,
                setAuthenticatedUser,
                startVerificationProcess,
                isOAuthUser,
                needsProfileCompletion,
                isSuspended
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;


